/* StageCharts — OpenCharts product world.
   Left column: positioning, callouts to the five surfaces.
   Right column: a flowchart canvas mockup with surface tabs
   and a "build with Theo" prompt bar. */
const SURFACES = [
  { key: 'flow',   label: 'Flowchart',     active: true  },
  { key: 'deck',   label: 'Presentation',  active: false },
  { key: 'page',   label: 'Vibe coding',   active: false },
  { key: 'pod',    label: 'Podcast',       active: false },
  { key: 'note',   label: 'Notes',         active: false },
];

const NODES = [
  { id: 'in',     label: 'Input',          x:  60, y: 140, kind: 'start' },
  { id: 'parse',  label: 'Theo · parse',   x: 240, y:  70, kind: 'ai' },
  { id: 'plan',   label: 'Theo · plan',    x: 240, y: 210, kind: 'ai' },
  { id: 'build',  label: 'Build canvas',   x: 460, y: 140, kind: 'task' },
  { id: 'out',    label: 'Output',         x: 660, y: 140, kind: 'end' },
];

const EDGES = [
  ['in', 'parse'],
  ['in', 'plan'],
  ['parse', 'build'],
  ['plan', 'build'],
  ['build', 'out'],
];

const NODE_W = 130;
const NODE_H = 44;

function nodeColor(kind) {
  if (kind === 'ai')    return 'var(--charts)';
  if (kind === 'start') return 'var(--theo-a)';
  if (kind === 'end')   return 'var(--theo-c)';
  return 'var(--ink)';
}

function nodeCenter(node) {
  return { cx: node.x + NODE_W / 2, cy: node.y + NODE_H / 2 };
}

export default function StageCharts() {
  const nodeById = Object.fromEntries(NODES.map((n) => [n.id, n]));
  return (
    <div
      style={{
        padding: '60px',
        height: '100%',
        minHeight: 700,
        background: 'radial-gradient(50% 70% at 100% 0%, rgba(200,255,26,0.10), transparent 60%), #07090A',
        display: 'grid',
        gridTemplateColumns: '1fr 1.6fr',
        gap: 60,
        alignItems: 'start',
      }}
    >
      <div>
        <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--charts)', marginBottom: 24 }}>
          ◇ OpenCharts · the AI-native workspace
        </div>
        <h3 style={{ fontFamily: "'Inter Tight'", fontWeight: 500, fontSize: 64, letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 24px' }}>
          opencharts<span style={{ color: 'var(--charts)' }}>.</span>
        </h3>
        <p style={{ color: 'var(--ink-mute)', fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>
          Flowcharts, presentations, vibe-coded landing pages,
          podcasts, and notes — one canvas, all powered by Theo.
          Describe the workflow and it builds.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            'Flowcharts — describe a process, get the diagram',
            'Presentations — the canvas becomes a deck',
            'Vibe coding — flowchart becomes a working page',
            'Podcasts — turn notes or a flow into an episode',
            'Notes — long-form knowledge, structured by Theo',
          ].map((t) => (
            <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: 'var(--ink)' }}>
              <span style={{ color: 'var(--charts)' }}>→</span>
              {t}
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          background: '#0A0E0A',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 18,
          overflow: 'hidden',
        }}
      >
        {/* Toolbar */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>onboarding-flow.chart</div>
            <div style={{ padding: '3px 10px', borderRadius: 999, fontSize: 10, background: 'rgba(52,211,153,0.15)', color: '#34d399', fontFamily: "'JetBrains Mono'", border: '1px solid rgba(52,211,153,0.3)' }}>
              live
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ padding: '6px 12px', borderRadius: 8, background: 'linear-gradient(135deg, var(--charts), #E6FF66)', border: 'none', color: '#0A0F0A', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              ◇ Ask Theo
            </button>
            <button style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--ink)', fontSize: 11, cursor: 'pointer' }}>
              Export
            </button>
          </div>
        </div>
        {/* Surface tabs */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 6, overflowX: 'auto' }}>
          {SURFACES.map((s) => (
            <div
              key={s.key}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 11,
                fontFamily: "'JetBrains Mono'",
                whiteSpace: 'nowrap',
                background: s.active ? 'rgba(200,255,26,0.14)' : 'rgba(255,255,255,0.03)',
                color: s.active ? 'var(--charts)' : 'var(--ink-mute)',
                border: s.active ? '1px solid rgba(200,255,26,0.4)' : '1px solid var(--line)',
              }}
            >
              {s.label}
            </div>
          ))}
        </div>
        {/* Canvas */}
        <div style={{ position: 'relative', height: 320, background: 'radial-gradient(circle at center, #0E140A 0%, #07090A 100%)' }}>
          <svg width="100%" height="100%" viewBox="0 0 800 320" preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0 }}>
            {/* Subtle grid */}
            <defs>
              <pattern id="oc-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M40 0 L0 0 0 40" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="800" height="320" fill="url(#oc-grid)" />
            {/* Edges */}
            {EDGES.map(([fromId, toId]) => {
              const from = nodeCenter(nodeById[fromId]);
              const to = nodeCenter(nodeById[toId]);
              const midX = (from.cx + to.cx) / 2;
              const path = `M ${from.cx} ${from.cy} C ${midX} ${from.cy}, ${midX} ${to.cy}, ${to.cx} ${to.cy}`;
              return (
                <path key={`${fromId}-${toId}`} d={path} stroke="rgba(200,255,26,0.55)" strokeWidth="1.5" fill="none" />
              );
            })}
            {/* Nodes */}
            {NODES.map((n) => (
              <g key={n.id} transform={`translate(${n.x}, ${n.y})`}>
                <rect
                  width={NODE_W}
                  height={NODE_H}
                  rx="10"
                  fill="rgba(15,21,48,0.7)"
                  stroke={nodeColor(n.kind)}
                  strokeOpacity="0.6"
                  strokeWidth="1.2"
                />
                <circle cx="14" cy={NODE_H / 2} r="4" fill={nodeColor(n.kind)} />
                <text
                  x="28"
                  y={NODE_H / 2 + 4}
                  fontFamily="'Inter Tight', sans-serif"
                  fontSize="13"
                  fontWeight="500"
                  fill="var(--ink)"
                >
                  {n.label}
                </text>
              </g>
            ))}
          </svg>
        </div>
        {/* Prompt bar */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--charts)', fontFamily: "'JetBrains Mono'", fontSize: 12 }}>◇</span>
          <div style={{ flex: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 999, fontSize: 12, color: 'var(--ink-mute)', border: '1px solid rgba(255,255,255,0.06)', fontFamily: "'JetBrains Mono'" }}>
            Build a flowchart for a 3-step onboarding…
          </div>
          <span style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.06)', fontSize: 10, fontFamily: "'JetBrains Mono'", color: 'var(--ink-mute)' }}>⌘K</span>
        </div>
      </div>
    </div>
  );
}
